import { Component, OnInit, Input, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { FlyToNode } from 'src/app/engine/state/engine.actions';
import { Store, Select } from '@ngxs/store';
import { EngineState } from 'src/app/engine/state/engine.state';
import { BdGraphService } from 'src/lib/graph/graph.service';
import { BdGraph } from 'src/lib/graph/graph.model';
import { SetObjectVisibility, SetObjectPickable } from 'src/app/edit/state/edit.actions';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EngineNodesState } from 'src/app/engine/state/nodes/engine.nodes.state';
import {
  CollapseNode,
  ExpandNode,
  CheckNode,
  CheckInverseNodes,
  SetCheckedNodes, 
  HideInverse,
  ToggleInversePickableNodes
} from 'src/app/engine/state/nodes/engine.nodes.actions';
import { TreeTheme } from '../tree/tree.component';
import { GraphUtils } from 'src/lib/graph/graph-utils';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-tree-nested-node',
  templateUrl: './tree-nested-node.component.html',
  styleUrls: ['./tree-nested-node.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TreeNestedNodeComponent implements OnInit, OnDestroy {
  treeTheme = TreeTheme;
  isLoft = environment.exportType === 'module';

  @Select(EngineNodesState.getCheckedNodeIds)
  checkedNodesIds$: Observable<string[]>;

  @Input()
  graph: BdGraph<ViewTreeNode>;

  @Input()
  public get node(): ViewTreeNode {
    return this._node;
  }
  public set node(value: ViewTreeNode) {
    this._node = value;
    this.sanitizedID = this.graphService.sanitizeNodeID(this.node.id);
  }
  private _node: ViewTreeNode;

  @Input()
  nested = true;

  @Input()
  theme: TreeTheme = TreeTheme.Dark;
  
  checked = false;
  
  // DOM friendly version of node ID
  // nodeID may include pipe characters, which cause errors with querySelector
  sanitizedID = '';

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private graphService: BdGraphService,
    private changeDetection: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.checkedNodesIds$
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(nodeIds => {
        this.checked = nodeIds.some((id) => id === this.node.id);
        this.changeDetection.markForCheck();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleExpandClick(node: ViewTreeNode, expanded: boolean) {
    (expanded) ?
      this.store.dispatch(new CollapseNode(node)) :
      this.store.dispatch(new ExpandNode(node));
  }
  
  handleLabelClick(event: MouseEvent, node: ViewTreeNode) {
    if (event.shiftKey) {
      this.checkNode(node);
    } else {
      this.setCheckedNodes(node);
    }
  }

  isChecked(node) {
    return this.store.selectSnapshot(EngineNodesState.getCheckedNodes)
      .some(checkedNode => checkedNode.id === node.id);
  }

  checkNode(node: ViewTreeNode, checked?: boolean) {
    if (typeof checked === 'undefined') {
      // When not explicitly defiend, toggle the node checked state
      checked = !this.isChecked(node);  
    }

    this.store.dispatch(new CheckNode(node, checked));
  }

  checkInverse(node) {
    this.store.dispatch(new CheckInverseNodes([node]));
  }

  setCheckedNodes(node: ViewTreeNode, checked?: boolean) {
    if (typeof checked === 'undefined') {
      // When not explicitly defiend, toggle the node checked state
      checked = !this.isChecked(node);  
    }
 
    const nodes = checked ? [node] : [];
    this.store.dispatch(new SetCheckedNodes(nodes));
  }

  countChildren(node: ViewTreeNode) {
    return GraphUtils.getSuccessors(this.graph, node.id).length;
  }

  hasChildren(node: ViewTreeNode) {    
    return this.countChildren(node) > 0;
  }

  flyToNode(node: ViewTreeNode) {
    this.store.dispatch(new FlyToNode(node));
  }
  
  hideNode(node: ViewTreeNode) {
    this.store.dispatch(new SetObjectVisibility([node], false));
  }

  hideInverse(node: ViewTreeNode) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    this.store.dispatch(new HideInverse([node], viewGraph));
  }
  
  showNode(node: ViewTreeNode) {
    this.store.dispatch(new SetObjectVisibility([node], true));
    this.changeDetection.markForCheck();
  }

  toggleVisible(node: ViewTreeNode, show?: boolean) {
    show = (typeof show === 'undefined') ? !node.boolState.shown : show;
    this.store.dispatch(new SetObjectVisibility([node], show));
    this.changeDetection.markForCheck();
  }

  togglePickable(node: ViewTreeNode, pickable: boolean) {
    this.store.dispatch(new SetObjectPickable([node], pickable));
    this.changeDetection.markForCheck();
  }

  toggleInversePickable(node: ViewTreeNode, pickable: boolean) {
    const viewGraph = this.store.selectSnapshot(EngineState.getViewGraph);
    this.store.dispatch(new ToggleInversePickableNodes([node], viewGraph, pickable));    
  }

}

